import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Plus, 
  Lightbulb, 
  Sparkles, 
  Calendar, 
  Quote, 
  Radar, 
  ArrowRight, 
  ArrowLeft, 
  X,
  Check,
  FileText,
  Video,
  Zap,
  Target,
  Users,
  Share2,
  Crown,
  Gift,
  Star,
  DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";

const steps = [
  {
    id: "welcome",
    title: "Welcome to Viraly!",
    subtitle: "Your AI-powered social media assistant",
    description: "Let's take a quick tour to help you create amazing content in minutes.",
    icon: Sparkles,
    color: "from-viraly-orange to-orange-400",
    tips: [
      "Create posts, videos, ads & emails",
      "AI learns your brand voice automatically",
      "Smart scheduling & viral optimization"
    ]
  },
  {
    id: "brand",
    title: "Brand Setup",
    subtitle: "AI learns your brand automatically",
    description: "Complete the setup wizard on your dashboard. Paste your website URL and our AI will learn your brand voice, audience, and style.",
    icon: Sparkles,
    color: "from-purple-500 to-purple-400",
    tips: [
      "Paste any URL to learn your brand",
      "Upload headshot for personal branding",
      "Your brand appears on all content"
    ]
  },
  {
    id: "create",
    title: "Create Content",
    subtitle: "Multiple ways to create posts",
    description: "Start from a URL, write your own, upload images (up to 6!), or let AI generate ideas. Use our 500 FREE templates for professional designs!",
    icon: Plus,
    color: "from-blue-500 to-blue-400",
    path: "/create",
    tips: [
      "Paste any URL + add instructions",
      "500 FREE templates (20 categories)",
      "Upload multiple images with scroll preview"
    ]
  },
  {
    id: "templates",
    title: "500 FREE Templates",
    subtitle: "Professional designs included",
    description: "Choose from 500 templates across Property, Product, Restaurant, Jobs, Events, and more. Enter your details and see real-time position preview!",
    icon: FileText,
    color: "from-green-500 to-green-400",
    path: "/create?useTemplate=true",
    isNew: true,
    tips: [
      "20 categories for any business",
      "Customize fonts, colors & positions",
      "FREE with every plan - no extra cost!"
    ]
  },
  {
    id: "ideas",
    title: "Content Ideas",
    subtitle: "Never run out of inspiration",
    description: "Select your niche or use templates to generate AI-powered content ideas tailored to your audience. Track competitors too!",
    icon: Lightbulb,
    color: "from-yellow-500 to-yellow-400",
    path: "/ideas",
    tips: [
      "AI Generate, Templates, or Custom",
      "Track competitors (1-10 based on plan)",
      "Post or video content"
    ]
  },
  {
    id: "viral",
    title: "Viral Simulator",
    subtitle: "Optimize before you post",
    description: "Test your content's viral potential and get real-time suggestions.",
    icon: Zap,
    color: "from-purple-500 to-purple-400",
    path: "/viral-simulator",
    free: true,
    tips: [
      "Real-time viral score",
      "Hashtag suggestions",
      "Caption optimization"
    ]
  },
  {
    id: "rewards",
    title: "Rewards Center",
    subtitle: "Earn discounts & free credits",
    description: "Visit your Rewards Center to track your loyalty tier, referral earnings, and influencer status. The more you use Viraly, the more you save!",
    icon: Gift,
    color: "from-viraly-orange to-yellow-400",
    path: "/rewards",
    isNew: true,
    tips: [
      "Loyalty tiers: Bronze → Diamond (5-25% off)",
      "Refer friends: Earn free post credits",
      "Become an Influencer: Earn 10% commission"
    ]
  },
  {
    id: "referral",
    title: "Referral Program",
    subtitle: "Share & earn free credits",
    description: "Get your unique referral code and QR code from the Rewards Center. When friends sign up and purchase, you get free post credits!",
    icon: Share2,
    color: "from-blue-500 to-cyan-400",
    path: "/rewards",
    tips: [
      "Share your unique code or QR",
      "Friends get 10% off first purchase",
      "You earn free post credits per referral"
    ]
  },
  {
    id: "influencer",
    title: "Become an Influencer",
    subtitle: "Earn real money from referrals",
    description: "Top referrers can qualify for our Influencer Program and earn 10% commission on ALL purchases made by their referrals. Request payouts via PayPal or e-Transfer!",
    icon: DollarSign,
    color: "from-green-500 to-emerald-400",
    path: "/rewards",
    isNew: true,
    tips: [
      "Meet referral thresholds to qualify",
      "Earn 10% lifetime commission",
      "Cash out via PayPal or e-Transfer"
    ]
  },
  {
    id: "credits",
    title: "Credits & Pricing",
    subtitle: "Your Content Allowance",
    description: "Posts, videos, and ad creatives use credits. Email generation is FREE with any post! Loyalty discounts stack with promotions.",
    icon: Crown,
    color: "from-viraly-orange to-orange-400",
    tips: [
      "Posts & Ads = 1 credit each",
      "Videos = video credits",
      "Loyalty discounts: Up to 25% off!"
    ]
  },
  {
    id: "ready",
    title: "You're All Set!",
    subtitle: "Start creating amazing content",
    description: "You're ready to go viral! Complete the brand setup, create your first post, and share your referral code to earn rewards.",
    icon: Check,
    color: "from-viraly-success to-green-400",
    tips: [
      "Complete brand setup first",
      "Create your first branded post",
      "Share your referral code for free credits"
    ]
  }
];

export default function OnboardingGuide({ isOpen, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      localStorage.setItem("viraly_onboarding_complete", "true");
      onComplete?.();
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem("viraly_onboarding_complete", "true");
    onComplete?.();
    onClose();
  };

  const handleGoTo = (path) => {
    localStorage.setItem("viraly_onboarding_complete", "true");
    onComplete?.();
    onClose();
    navigate(path);
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      >
        <motion.div
          key={step.id}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-viraly-card rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl border border-white/10"
        >
          {/* Progress Bar */}
          <div className="h-1 bg-white/10">
            <motion.div
              className="h-full bg-viraly-gradient"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Header */}
          <div className="p-6 pb-0 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-viraly-muted">
              <span>Step {currentStep + 1} of {steps.length}</span>
            </div>
            <button
              onClick={handleSkip}
              className="text-viraly-muted hover:text-white text-sm transition-colors"
              data-testid="skip-onboarding"
            >
              Skip Tour
            </button>
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            {/* Icon */}
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
              <step.icon className="w-10 h-10 text-white" />
            </div>

            {/* Title */}
            <h2 className="font-heading text-2xl font-bold mb-1">{step.title}</h2>
            <p className="text-viraly-orange font-medium text-sm mb-3">{step.subtitle}</p>
            <p className="text-viraly-muted mb-6">{step.description}</p>

            {/* Tips */}
            <div className="bg-white/5 rounded-xl p-4 text-left space-y-3 mb-6">
              {step.tips.map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm text-viraly-light">{tip}</span>
                </div>
              ))}
            </div>

            {/* Free Badge */}
            {step.free && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-viraly-success/20 text-viraly-success text-xs font-medium mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                Free Feature - No Credits Required!
              </div>
            )}

            {/* Go To Button (if applicable) */}
            {step.path && !isLastStep && (
              <Button
                onClick={() => handleGoTo(step.path)}
                variant="ghost"
                className="text-viraly-orange hover:bg-viraly-orange/10 mb-2"
                data-testid={`goto-${step.id}`}
              >
                Try it now <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 pt-0 flex items-center justify-between gap-4">
            <Button
              onClick={handlePrev}
              disabled={isFirstStep}
              variant="ghost"
              className={`${isFirstStep ? 'invisible' : ''}`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {/* Dots */}
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentStep(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentStep 
                      ? "w-6 bg-viraly-orange" 
                      : i < currentStep 
                        ? "bg-viraly-orange/50" 
                        : "bg-white/20"
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={handleNext}
              className="btn-primary"
              data-testid="next-step"
            >
              {isLastStep ? (
                <>
                  Get Started
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
